import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { Button, FormItem, TextField, Typogaphy } from 'vnetwork-ui'
import { Control, Controller, useWatch } from 'react-hook-form'
import { Loader2, CheckCircle2 } from 'lucide-react'
import toast from 'react-hot-toast'
import SettingIcons from '@/icons/SettingIcons'
import { testSmtpConnection } from '../../../services'

interface SmtpSettingsProps {
    control: Control<any>
    smtpEnabled: boolean
    onTestStatusChange?: (tested: boolean) => void
}

const SmtpSettings: React.FC<SmtpSettingsProps> = ({
    control,
    smtpEnabled,
    onTestStatusChange,
}) => {
    const { t } = useTranslation()
    const [isPending, setIsPending] = useState(false)
    const [isTested, setIsTested] = useState(false)

    // Default SMTP username from env
    const defaultSmtpUsername =
        import.meta.env.APP_DEFAULT_SMTP_USERNAME || 'no-reply@vnetwork.vn'

    // Watch SMTP fields
    const smtpUsername = useWatch({
        control,
        name: 'metadata.mailer.user_name',
    })
    const smtpPassword = useWatch({ control, name: 'metadata.mailer.password' })

    // Check if using custom SMTP (different from default)
    const isCustomSmtp = smtpUsername && smtpUsername !== defaultSmtpUsername

    // Reset test status when credentials change
    useEffect(() => {
        // If using default SMTP, consider it as tested
        if (!isCustomSmtp) {
            setIsTested(true)
            onTestStatusChange?.(true)
        } else {
            setIsTested(false)
            onTestStatusChange?.(false)
        }
    }, [smtpUsername, smtpPassword, isCustomSmtp])

    const handleTestSmtp = async (username: string, password: string) => {
        if (isPending) return
        setIsPending(true)
        try {
            const response = await testSmtpConnection({
                smtp: { user_name: username, password },
            })
            toast.success(
                response.message || t('mailerCampaign:smtp_connection_success')
            )
            setIsTested(true)
            onTestStatusChange?.(true)
        } catch (error: any) {
            toast.error(
                error?.response?.data?.message ||
                    error?.response?.data?.error_description ||
                    t('mailerCampaign:smtp_connection_failed')
            )
            setIsTested(false)
            onTestStatusChange?.(false)
        } finally {
            setIsPending(false)
        }
    }

    return (
        <div className="space-y-3 pt-4 border-t" data-tour="smtp-settings">
            <div className="flex items-center gap-2">
                <SettingIcons className="text-primary w-4 h-4" />
                <Typogaphy className="font-medium capitalize">
                    SMTP Sender
                </Typogaphy>
                <Controller
                    name="smtp_enabled"
                    control={control}
                    render={({ field }) => (
                        <Typogaphy
                            className="text-primary underline cursor-pointer"
                            onClick={() => field.onChange(!field.value)}
                        >
                            {t('mailerCampaign:use_custom_smtp')}
                        </Typogaphy>
                    )}
                />
            </div>
            {smtpEnabled && (
                <div className="p-4 bg-gray-50 rounded-md">
                    <div className="grid grid-cols-1 gap-4">
                        <FormItem
                            control={control}
                            name="metadata.mailer.address"
                            type="textfield"
                            label={t('mailerCampaign:smtp_host')}
                            placeholder="mail.example.com"
                        />
                        <FormItem
                            control={control}
                            name="metadata.mailer.user_name"
                            type="textfield"
                            label={t('mailerCampaign:smtp_username')}
                            placeholder="user@example.com"
                        />
                        <Controller
                            name="metadata.mailer.password"
                            control={control}
                            render={({ field, fieldState: { error } }) => (
                                <TextField
                                    label={t('mailerCampaign:smtp_password')}
                                    type="password"
                                    size="small"
                                    {...field}
                                    placeholder={t(
                                        'mailerCampaign:smtp_password'
                                    )}
                                    className="w-full"
                                    error={!!error}
                                    helperText={error?.message}
                                />
                            )}
                        />
                        {isCustomSmtp ? (
                            <div className="flex items-center gap-2">
                                <Button
                                    variant={
                                        isTested ? 'contained' : 'outlined'
                                    }
                                    color={isTested ? 'success' : 'primary'}
                                    size="small"
                                    onClick={() =>
                                        handleTestSmtp(
                                            smtpUsername,
                                            smtpPassword
                                        )
                                    }
                                    type="button"
                                    disabled={
                                        isPending ||
                                        !smtpUsername ||
                                        !smtpPassword
                                    }
                                >
                                    {isPending && (
                                        <Loader2
                                            size={16}
                                            className="animate-spin mr-1"
                                        />
                                    )}
                                    {isTested && (
                                        <CheckCircle2
                                            size={16}
                                            className="mr-1"
                                        />
                                    )}
                                    {isTested
                                        ? t('mailerCampaign:smtp_tested')
                                        : t(
                                              'mailerCampaign:test_smtp_connection'
                                          )}
                                </Button>
                            </div>
                        ) : (
                            <div className="flex items-center gap-2 text-sm text-green-600">
                                <CheckCircle2 size={16} />
                                <span>
                                    {t('mailerCampaign:using_default_smtp')}
                                </span>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    )
}

export default SmtpSettings
